'use client';

import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import type { SSEMessage } from './use-sse.types';

const SSEContext = createContext<{ messages: SSEMessage[]; start: () => void }>(
  {
    messages: [],
    start: () => undefined,
  },
);

export const useSSE = () => useContext(SSEContext);

export function SSEProvider({
  children,
  treatment,
  sessionId,
  sseUrl,
}: {
  children: ReactNode;
  treatment?: string;
  sessionId?: string;
  sseUrl: string;
}) {
  const [messages, setMessages] = useState<Array<SSEMessage>>([]);
  const [start, setStart] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    if (treatment === 'variant' && sessionId && start) {
      const url = sseUrl + '/api/third-party/v1/calls/events/' + sessionId;

      const connect = () => {
        console.log('[SSE] Connecting to:', url);
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;

        eventSource.onopen = function () {
          console.log('[SSE] Connection opened successfully');
        };

        // Listen for 'call-status-update' events (the main event from the server)
        eventSource.addEventListener('call-status-update', (event) => {
          console.log('[SSE] Received call-status-update:');
          try {
            const parsedData: SSEMessage = JSON.parse(event.data);
            console.log('[SSE] Received call-status-update:', parsedData);
            if (parsedData) {
              setMessages((data) => [parsedData, ...data]);
            }
          } catch (error) {
            console.error('[SSE] Failed to parse call-status-update:', error);
          }
        });

        // Listen for 'heartbeat' events
        eventSource.addEventListener('heartbeat', (event) => {
          console.log('[SSE] Received heartbeat:', event.data);
          // Heartbeats are just for keeping the connection alive
          // Optionally you can handle them differently
        });

        eventSource.onerror = (error) => {
          console.error('[SSE] Connection error:', error);
          console.error('[SSE] ReadyState:', eventSource.readyState);

          if (eventSource.readyState === EventSource.CLOSED) {
            console.log(
              '[SSE] Connection closed by server, will reconnect in 3s...',
            );
            eventSource.close();
            eventSourceRef.current = null;

            // Reconnect after 3 seconds
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('[SSE] Reconnecting...');
              connect();
            }, 3000);
          } else if (eventSource.readyState === EventSource.CONNECTING) {
            console.log('[SSE] Attempting to reconnect...');
          }
        };
      };

      // Initial connection
      connect();

      return () => {
        console.log('[SSE] Cleaning up and closing connection');

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      };
    }

    return undefined;
  }, [sessionId, start, treatment, sseUrl]);

  return (
    <SSEContext.Provider
      value={{
        messages,
        start: () => setStart(true),
      }}
    >
      {children}
    </SSEContext.Provider>
  );
}
